import { logging } from '@js-sdk/debug-tools';
import { EventEmitter } from '@spotify-internal/emitter';
import { Event } from '../enums/event';
import { TrackingData, } from './tracking_data';
const debugLogger = logging.forTag('playback.tracker');
/**
 * The threshold value for playback.
 */
const PLAYED_THRESHOLD = 31000;
/**
 * Tracks the information about a playback session.
 */
export class Tracker extends EventEmitter {
    // _totalStreamTime is defined but never used in the class
    // private _totalStreamTime: number = 0;
    constructor(options = {}) {
        super();
        this._playedThreshold = PLAYED_THRESHOLD;
        this._playedThresholdReached = false;
        this._trackingData = null;
        this._playedThreshold = options.playerThreshold || PLAYED_THRESHOLD;
    }
    static create(options) {
        return new Tracker(options);
    }
    /**
     * Function for creating the checking whether the played threshold have been
     * reached.
     * If it has the Event.TRACKER_PLAYED_THRESHOLD_REACHED will be emitted.
     */
    _checkPlayedThreshold() {
        const trackingData = this._trackingData;
        if (!trackingData ||
            !this._playedThreshold ||
            this._playedThresholdReached ||
            trackingData.msPlayed < this._playedThreshold) {
            return;
        }
        this._playedThresholdReached = true;
        this.emit(Event.TRACKER_PLAYED_THRESHOLD_REACHED, {
            played: (trackingData && trackingData.msPlayed) || 0,
            threshold: this._playedThreshold,
        });
    }
    /**
     * Returns the amount played in milliseconds.
     *
     * @return The amount played in ms.
     */
    getMSPlayed() {
        if (!this._trackingData) {
            return 0;
        }
        return this._trackingData.msPlayed;
    }
    /**
     * Function for tracking the start of loading a track.
     *
     * @param uri - The track uri.
     * @param fileId - The tracks file id.
     * @param playId - The internal playId.
     * @param logData - The tracks log data.
     */
    trackLoadStart(uri, fileId, playId, logData) {
        const trackingData = TrackingData.create();
        this._trackingData = trackingData;
        this._playedThresholdReached = false;
        trackingData.fileId = fileId;
        trackingData.currentTrackUri = uri;
        trackingData.loadTime = Date.now();
        trackingData.internalPlayId = playId;
        trackingData.externalLoadTime = logData.externalLoadTime || 0;
        trackingData.displayTrack = logData.displayTrack || "" /* EMPTY */;
        trackingData.playbackId = logData.playbackId || "" /* EMPTY */;
        trackingData.playContext = logData.playContext || "" /* EMPTY */;
        trackingData.reasonStart = logData.reason || "unknown" /* UNKNOWN */;
        trackingData.sourceStart = logData.source || "unknown" /* UNKNOWN */;
        const referrerData = logData.referrer || {};
        trackingData.referrer =
            typeof referrerData.name !== 'undefined'
                ? referrerData.name
                : "unknown" /* UNKNOWN */;
        trackingData.referrerVersion =
            typeof referrerData.version !== 'undefined'
                ? referrerData.version
                : "unknown" /* UNKNOWN */;
        trackingData.referrerVendor =
            typeof referrerData.vendor !== 'undefined'
                ? referrerData.vendor
                : "unknown" /* UNKNOWN */;
        const format = logData.format || {};
        if (format.codec) {
            trackingData.audiocodec = format.codec.toLowerCase();
        }
        if (format.bitrate) {
            trackingData.bitrate = format.bitrate;
        }
        trackingData.gaiaDevId = logData.deviceId || "none" /* NONE */;
        // Check this if it's some errors
        trackingData.noLog = logData.noLog || false;
        trackingData.noTSV = logData.noTSV || false;
        trackingData.noStats = logData.noStats || false;
        this.emit(Event.TRACKER_TRACKING_DATA_CREATED, {
            trackingData: trackingData,
        });
    }
    /**
     * Function for tracking when the first bytes of the file (init and first
     * playable segment) were triggered to be loaded.
     */
    trackBufferLoadStart() {
        const trackingData = this._trackingData;
        if (!trackingData) {
            return;
        }
        trackingData.bufferLoadStartTime = Date.now();
    }
    /**
     * Function for adding to the amount of bytes downloaded for a track.
     *
     * @param nBytes - The number of bytes to append to the tracking data.
     */
    trackBytesDownloaded(nBytes) {
        const trackingData = this._trackingData;
        if (!trackingData) {
            return;
        }
        trackingData.totalBytes += nBytes;
    }
    /**
     * Function for tracking that a track has finished loading.
     *
     * @param position - The position at which playback was initiated.
     */
    trackLoadDone(position) {
        const trackingData = this._trackingData;
        if (!trackingData) {
            return;
        }
        const timestamp = Date.now();
        trackingData.msInitLatency = timestamp - trackingData.bufferLoadStartTime;
        trackingData.lastPlayStartTime = timestamp;
        trackingData.localTimeMs = timestamp;
        trackingData.position = position;
        trackingData.startPosition = position;
        trackingData.addStartSegment();
    }
    /**
     * Function for tracking that a track can start playing.
     */
    trackCanPlayThrough() {
        const trackingData = this._trackingData;
        if (!trackingData || trackingData.msPlayLatency) {
            return;
        }
        const timestamp = Date.now();
        trackingData.msPlayLatency = timestamp - trackingData.loadTime;
        trackingData.msHeadLatency = timestamp - trackingData.bufferLoadStartTime;
    }
    /**
     * Tracks the use of a cdn URL.
     *
     * @param url - The url to track.
     * @param data - The data for the cdn URL.
     */
    trackBufferURL(url, data) {
        const trackingData = this._trackingData;
        if (!trackingData) {
            return;
        }
        trackingData.trackBufferURL(url, data);
    }
    /**
     * Tracks the use of a cdn URL.
     */
    trackNavigatorOffline() {
        const trackingData = this._trackingData;
        if (!trackingData) {
            return;
        }
        trackingData.nOffline += 1;
    }
    /**
     * Function for tracking the time in milliseconds spent waiting for
     * re-buffering on seek.
     *
     * @param ms - The number of milliseconds spent buffering.
     */
    trackSeekRebuffering(ms) {
        const trackingData = this._trackingData;
        if (!trackingData) {
            return;
        }
        if (ms > trackingData.maxMsSeekRebuffering) {
            trackingData.maxMsSeekRebuffering = ms;
        }
        trackingData.msSeekRebuffering += ms;
    }
    /**
     * Function for tracking the time in milliseconds with playback stalled due
     * to buffer underruns.
     *
     * @param ms - The number of milliseconds spent buffering.
     */
    trackMsStalled(ms) {
        const trackingData = this._trackingData;
        if (!trackingData) {
            return;
        }
        if (ms > trackingData.maxMsStalled) {
            trackingData.maxMsStalled = ms;
        }
        trackingData.msStalled += ms;
        trackingData.nStalls++;
    }
    /**
     * Function for tracking a track failing to load.
     */
    trackLoadFailed() {
        const trackingData = this._trackingData;
        if (!trackingData) {
            return;
        }
        trackingData.msPlayLatency = Date.now() - trackingData.loadTime;
        trackingData.position = 0;
        trackingData.addStartSegment();
    }
    /**
     * Function for tracking the stopping of a track.
     *
     * @param logData - The tracks log data.
     */
    trackStopped(logData) {
        const trackingData = this._trackingData;
        if (!trackingData || trackingData.isFinalized()) {
            return;
        }
        const timestamp = Date.now();
        trackingData.stopTime = timestamp;
        if (trackingData.lastPlayStartTime) {
            trackingData.addMSPlayed(timestamp - trackingData.lastPlayStartTime);
            this._checkPlayedThreshold();
        }
        trackingData.addEndSegment();
        trackingData.sourceEnd = logData.source || "unknown" /* UNKNOWN */;
        trackingData.reasonEnd = logData.reason || "unknown" /* UNKNOWN */;
        trackingData.nextPlaybackId = logData.playbackId || "" /* EMPTY */;
        trackingData.finalize();
        // this._totalStreamTime is commented out since it's never used
        // this._totalStreamTime += trackingData.msPlayed;
        debugLogger.info('Tracker data finalized.');
        const playbackStats = trackingData.getPlaybackStats();
        this.emit(Event.TRACKER_TRACKING_DATA_FINALIZED, {
            trackingData: trackingData,
            playbackStats: playbackStats,
        });
    }
    /**
     * Function for tracking position changes that don't come from regular
     * progress events, such as seeks.
     *
     * @param newPosition - The position moved to.
     * @param paused - True if playback was paused when when the change
     * in position took place.
     */
    trackPositionChanged(newPosition, paused) {
        const trackingData = this._trackingData;
        if (!trackingData) {
            return;
        }
        const currentPosition = trackingData.position;
        if (newPosition === currentPosition) {
            return;
        }
        trackingData.addEndSegment();
        if (newPosition > currentPosition) {
            trackingData.nSeeksForward++;
            trackingData.msSeeksForward += newPosition - currentPosition;
        }
        else if (newPosition < currentPosition) {
            trackingData.nSeeksBackward++;
            trackingData.msSeeksBackward += currentPosition - newPosition;
        }
        if (!paused) {
            trackingData.lastPlayStartTime = Date.now();
        }
        trackingData.position = newPosition;
        trackingData.addStartSegment();
    }
    /**
     * Function for tracking that a track is playing.
     *
     * @param position - The position at which the track started playing.
     */
    trackPlaying(position) {
        const trackingData = this._trackingData;
        if (!trackingData) {
            return;
        }
        if (!trackingData.played) {
            // Track only first time playback start
            this.emit(Event.TRACKER_PLAYBACK_START, {
                trackingData: trackingData,
            });
        }
        trackingData.played = true;
        trackingData.lastPlayStartTime = Date.now();
        trackingData.position = position;
    }
    /**
     * Function for tracking that a track is paused.
     *
     * @param position - The position at which the track was paused.
     */
    trackPaused(position) {
        const trackingData = this._trackingData;
        if (!trackingData) {
            return;
        }
        trackingData.lastPlayStartTime = 0;
        trackingData.position = position;
    }
    /**
     * Function for tracking the progress of a tracks playback.
     *
     * @param position - The current position of the play head in the
     * track.
     * @param bitrate - The current bitrate for the playing segment.
     */
    trackProgress(position, bitrate) {
        const trackingData = this._trackingData;
        if (!trackingData) {
            return;
        }
        const timestamp = Date.now();
        let offset = 0;
        if (trackingData.lastPlayStartTime) {
            offset = timestamp - trackingData.lastPlayStartTime;
        }
        trackingData.lastPlayStartTime = timestamp;
        trackingData.addMSPlayed(offset);
        this._checkPlayedThreshold();
        trackingData.position = position;
        if (bitrate) {
            trackingData.trackBitrate(bitrate, offset);
        }
    }
    /**
     * Function for setting initial properties for video tracks.
     *
     * @param data - The profile data for the upcoming video track.
     */
    trackVideoLoadStart(data) {
        const trackingData = this._trackingData;
        if (!trackingData) {
            return;
        }
        trackingData.registerVideoVariant(data);
    }
    /**
     * Function for incrementing the warnings.
     */
    trackWarning() {
        const trackingData = this._trackingData;
        if (!trackingData) {
            return;
        }
        trackingData.nWarnings++;
    }
    /**
     * Function for incrementing offline counts.
     */
    trackOffline() {
        const trackingData = this._trackingData;
        if (!trackingData) {
            return;
        }
        trackingData.nOffline++;
    }
    // SIMPLE SETTERS
    /**
     * Function for tracking the current estimated duration.
     *
     * @param msDuration - The length of the media currently loaded in
     * milliseconds.
     */
    setActualDuration(msDuration) {
        const trackingData = this._trackingData;
        if (!trackingData) {
            return;
        }
        trackingData.msActualDuration = msDuration;
    }
    /**
     * Function for tracking which key system is used to decrypt the currently
     * playing track.
     *
     * @param keySystem - The keySystem used to decrypt the track.
     */
    setKeySystem(keySystem) {
        const trackingData = this._trackingData;
        if (!trackingData) {
            return;
        }
        trackingData.keySystem = keySystem;
    }
    /**
     * Tracks the implementation of the EME keysystem used.
     *
     * @param keySystemImpl - The keysystem implemenation description.
     */
    setKeySystemImpl(keySystemImpl) {
        const trackingData = this._trackingData;
        if (!trackingData) {
            return;
        }
        trackingData.keySystemImpl = keySystemImpl;
    }
    /**
     * Function for tracking the time it took for a track manifest to load.
     *
     * @param ms - The number of milliseconds it took for the manifest to
     * load.
     */
    setManifestLatency(ms) {
        const trackingData = this._trackingData;
        if (!trackingData) {
            return;
        }
        trackingData.msManifestLatency = ms;
    }
    /**
     * Function for tracking if the track play is intended.
     *
     * @param playIntended - True if play is intended.
     */
    setPlayIntended(playIntended) {
        const trackingData = this._trackingData;
        if (!trackingData) {
            return;
        }
        trackingData.playIntended = playIntended;
    }
    /**
     * Function for tracking the time it took to fetch and install the DRM for a
     * track.
     *
     * @param value - The time in milliseconds.
     */
    setKeyLatency(value) {
        const trackingData = this._trackingData;
        if (!trackingData) {
            return;
        }
        trackingData.msKeyLatency = value;
    }
    setMemoryCached(memoryCached) {
        const trackingData = this._trackingData;
        if (!trackingData) {
            return;
        }
        trackingData.memoryCached = memoryCached;
    }
    setPersistentCached(persistentCached) {
        const trackingData = this._trackingData;
        if (!trackingData) {
            return;
        }
        trackingData.persistentCached = persistentCached;
    }
    setProtected(isProtected) {
        const trackingData = this._trackingData;
        if (!trackingData) {
            return;
        }
        trackingData.isProtected = isProtected;
    }
    setHadError(hadError) {
        const trackingData = this._trackingData;
        if (!trackingData) {
            return;
        }
        trackingData.hadError = hadError;
    }
    setLicenseSessionLatency(latency) {
        const trackingData = this._trackingData;
        if (!trackingData) {
            return;
        }
        trackingData.msLicenseSessionLatency = latency;
    }
    setLicenseGenerationLatency(latency) {
        const trackingData = this._trackingData;
        if (!trackingData) {
            return;
        }
        trackingData.msLicenseGenerationLatency = latency;
    }
    setLicenseRequestLatency(latency) {
        const trackingData = this._trackingData;
        if (!trackingData) {
            return;
        }
        trackingData.msLicenseRequestLatency = latency;
    }
    setLicenseUpdateLatency(latency) {
        const trackingData = this._trackingData;
        if (!trackingData) {
            return;
        }
        trackingData.msLicenseUpdateLatency = latency;
    }
    /**
     * Function for tracking the time in milliseconds spent on the network when
     * resolving file urls.
     *
     * @param ms - The number of milliseconds.
     */
    setResolveLatency(ms) {
        const trackingData = this._trackingData;
        if (!trackingData) {
            return;
        }
        trackingData.msResolveLatency = ms;
    }
    /**
     * Function for tracking the calculated duration for a track.
     *
     * @param ms - The duration in milliseconds.
     */
    setCalculatedDuration(ms) {
        const trackingData = this._trackingData;
        if (!trackingData) {
            return;
        }
        trackingData.msFileDuration = ms;
    }
    /**
     * Sets the played threshold duration.
     *
     * @param ms - The duration in milliseconds.
     */
    setPlayedThreshold(ms) {
        this._playedThreshold = ms;
    }
}
//# sourceMappingURL=tracker.js.map